import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Paper, Typography, Box, Tabs, Tab } from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import PointsLeaderboardTab from '../../components/leaderboard/PointsLeaderboardTab';
import RankedLeaderboardTab from '../../components/leaderboard/RankedLeaderboardTab';

type LeaderboardTabKey = 'ranked' | 'points';

/**
 * `/leaderboard`, now a thin host over two tabs measuring different things —
 * Ranked is skill (Glicko rating from live quizzes), All-Time Points is
 * effort (problem-solving volume and accuracy) — so neither replaces the
 * other.
 *
 * Ranked is the default even on an empty season: a default that depends on
 * fetched data would flip after load, which reads as a flicker. The one
 * exception is a deep link carrying `sessionId` with no explicit `tab` —
 * that link only makes sense against Points, so it lands there instead.
 *
 * The host owns only the `tab` param and never writes it until the user
 * actually switches tabs (writing a default on mount would spam history).
 * Both tabs mutate only their own search-param keys for the same reason —
 * see PointsLeaderboardTab's session selector.
 */
function LeaderboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: LeaderboardTabKey =
    (searchParams.get('tab') as LeaderboardTabKey | null) ??
    (searchParams.has('sessionId') ? 'points' : 'ranked');

  const handleTabChange = (_event: React.SyntheticEvent, value: LeaderboardTabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next);
  };

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <TrophyIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h4" component="h1">
              Leaderboard
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Ranked measures skill from live quizzes. All-Time Points measures effort across every
            problem you've solved.
          </Typography>
        </Paper>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Ranked" value="ranked" />
          <Tab label="All-Time Points" value="points" />
        </Tabs>

        {activeTab === 'ranked' ? <RankedLeaderboardTab /> : <PointsLeaderboardTab />}
      </Container>
    </MainLayout>
  );
}

export default LeaderboardPage;
